import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import SignInForm from '../components/Form/SignInForm.js';
import SignUpForm from '../components/Form/SignUpForm.js';
import ForgotPasswordForm from '../components/Form/ForgotPasswordForm.js';
import { signin, signup } from '../actions/auth.js';
import { sendResetLink } from '../api/index.js';
import '../interfaceSettings.css';

const initialState = {
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    roleType: 'User',
    gender: '',
    dateOfBirth: '',
    ethnicity: '',
    community: '',
    agreementsAccepted: false
};
const Authorization = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [isSignup, setIsSignup] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [formData, setFormData] = useState(initialState);
    const [resetEmail, setResetEmail] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [ageError, setAgeError] = useState('');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Extract query parameter 'formType' from the URL
    const params = new URLSearchParams(location.search);
    const formType = params.get('formType');  // 'signin' or 'signup'

    // Set the initial form state based on the formType URL param, default to 'signin'
    useEffect(() => {
        if (formType === 'signup') {
            setIsSignup(true);
        } else {
            setIsSignup(false); // Default to signin if no query parameter or invalid parameter
        }
    }, [formType]);

    const handleShowPassword = () => setShowPassword((prev) => !prev);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrorMessage('');

        if (showForgotPassword) {
            try {
                console.log("Attempting to send reset link for email:", resetEmail);
                const response = await sendResetLink(resetEmail);
                if (response.status === 200) {
                    setSuccessMessage("Password reset link has been sent to your email.");
                    setShowForgotPassword(false); //Return to Sign In View
                } else {
                    console.error("Unexpected response:", response);
                }
            } catch (error) {
                console.error("Error sending reset link:", error);
                setErrorMessage("Email not found. Please try again.");
            }
        } else {
            if (isSignup) {
                // Ensure agreements are checked
                if (!formData.agreementsAccepted) {
                    setErrorMessage('You must accept the Privacy Agreement and Consent Agreement to create and account.');
                    return;
                }
                try {
                    const response = await dispatch(signup(formData));
                    // Handle conflicts
                    if (response?.data?.status === 'email_taken') {
                        setErrorMessage(response.data.message || "This e-mail is already being used.");
                        return;
                    }
                    if (response?.data?.status === 'username_taken') {
                        setErrorMessage(response.data.message || "This username is already being used.");
                        return;
                    }

                    // Handle success
                    if (response?.data?.status === "success") {
                        setSuccessMessage('Account created successfully! Redirecting...');
                        setTimeout(() => navigate('/authorization'), 3000); // Redirect to login
                        return;
                    }

                    // Handle unexpected errors
                    setErrorMessage(response?.error?.message || "Unexpected error occurred.");
                } catch (error) {
                    console.error("Error during sign up:", error);
                    setErrorMessage(error.response?.data?.message || "Unexpected error occurred. Email or username may already be in use. Please try again.");
                }
            } else {
                try {
                    await dispatch(signin(formData, navigate));
                } catch (error) {
                    console.error("Error during sign in:", error);
                    setSuccessMessage("");
                    setErrorMessage(error.response?.data?.message || "Sign In failed. Email or password not recognized. Please try again.");
                }
            }
        }
    };
    // Handle change
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type == 'checkbox' ? checked : value;
        setFormData((prev) => ({ ...prev, [name]: newValue }));
    };

    // Switch between Sign Up and Sign In and update the URL dynamically
    const switchMode = () => {
        setIsSignup((prev) => !prev);
        setShowPassword(false);
        setShowForgotPassword(false);
        setSuccessMessage('');
        setErrorMessage('');
        setAgeError('');
        setFormData(initialState);
        // Update the URL with the correct formType
        const newFormType = !isSignup ? 'signup' : 'signin';
        navigate(`/authorization?formType=${newFormType}`);
    };

    const showForgotPasswordForm = () => {
        setShowForgotPassword(true);
        setIsSignup(false);
        setShowPassword(false);
        setSuccessMessage('');
        setErrorMessage('');
        setFormData(initialState);
    };

    return (
        <div id="sign-in-container" className="container mt-5 mb-5">
            <div id="sign-in-card" className="card">
                <div id="sign-in-card-body" className="card-body">
                    <h1 id="sign-in-title" className="card-title text-center">
                        {showForgotPassword ? 'Reset Password' : (isSignup ? 'Sign Up' : 'Sign In')}
                    </h1>
                    {successMessage && (
                        <div className="alert alert-success text-center">
                            {successMessage}
                        </div>
                    )}
                    {errorMessage && (
                        <div className="alert alert-danger text-center">
                            {errorMessage}
                        </div>
                    )}
                    {ageError && (
                        <div className="alert alert-danger text-center">
                            {ageError}
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div id="sign-in-form-group" className="form-group">
                            {showForgotPassword ? (
                                <ForgotPasswordForm resetEmail={resetEmail} setResetEmail={setResetEmail} />
                            ) : isSignup ? (
                                <SignUpForm 
                                    formData={formData} 
                                    handleChange={handleChange} 
                                    handleShowPassword={handleShowPassword} 
                                    showPassword={showPassword}
                                    setAgeError={setAgeError}
                                />
                            ) : (
                                <SignInForm formData={formData} handleChange={handleChange} handleShowPassword={handleShowPassword} showPassword={showPassword} />
                            )}
                        </div>
                        <div className="d-flex justify-content-center align-items-center">
                            <button        
                                type="submit"
                                className="btn btn-primary btn-block btn-spacing">
                                {showForgotPassword ? 'Send Reset Link' : (isSignup ? 'Sign Up' : 'Sign In')}
                            </button>
                        </div>

                        {/* Don't show "Forgot Password" button in SignUp view */}
                        {!showForgotPassword && !isSignup && (
                            <div className="text-center mt-3">
                                <button
                                    id="forgot-password-btn"
                                    type="button"
                                    onClick={showForgotPasswordForm}
                                    className="btn btn-link">
                                    Forgot Password?
                                </button>
                            </div>
                        )}

                        {/* Only show switch between Sign In and Sign Up when not in Forgot Password view */}
                        {!showForgotPassword && (
                            <div className="text-center mt-3">
                                <button
                                    id="switch-mode-btn"
                                    type="button"
                                    onClick={switchMode}
                                    className="btn btn-link">
                                    {isSignup ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
                                </button>
                            </div>
                        )}

                        {showForgotPassword && (
                            <div className="text-center mt-3">
                                <button
                                    id="back-to-signin-btn"
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => setShowForgotPassword(false)}>
                                    Back to Sign In
                                </button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};
export default Authorization;